// frontend/src/components/visitor/CookieConsent.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Shield } from 'react-feather';
import { useTheme } from '../../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { themeClasses, colorTheme, darkMode } = useTheme();

  // Check if consent has been given previously
  useEffect(() => {
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      // Add a small delay before showing the banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Accept cookies
  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'true');
    setIsVisible(false);
  };

  // Get appropriate icon color based on theme
  const getIconColorClass = () => {
    // Map each theme to its color class
    const themeColorMap: Record<string, string> = {
      // Pastels
      'rose': 'text-rose-500 dark:text-rose-400',
      'lavender': 'text-purple-500 dark:text-purple-400',
      'mint': 'text-green-500 dark:text-green-400',
      'peach': 'text-orange-500 dark:text-orange-400',
      'sky': 'text-sky-500 dark:text-sky-400',
      
      // Rich colors
      'ruby': 'text-red-600 dark:text-red-500',
      'emerald': 'text-emerald-600 dark:text-emerald-500',
      'sapphire': 'text-blue-600 dark:text-blue-500',
      'amethyst': 'text-fuchsia-600 dark:text-fuchsia-500',
      'amber': 'text-amber-600 dark:text-amber-500',
      
      // Elegant
      'burgundy': 'text-red-800 dark:text-red-600',
      'navy': 'text-blue-800 dark:text-blue-600',
      'forest': 'text-green-800 dark:text-green-600',
      'gold': 'text-yellow-600 dark:text-yellow-500',
      'silver': 'text-gray-600 dark:text-gray-400'
    };
    
    return themeColorMap[colorTheme] || themeColorMap.rose;
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`fixed bottom-0 left-0 right-0 z-50 ${
            darkMode ? 'bg-gray-800 text-white border-t border-gray-700' : 'bg-white text-gray-800 shadow-lg border-t border-gray-200'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-start md:items-center mb-4 md:mb-0">
                <Shield className={`flex-shrink-0 mr-3 ${getIconColorClass()}`} size={24} />
                
                <div className="text-sm md:text-base">
                  <p className="font-medium">We value your privacy</p>
                  <p className="mt-1 text-gray-600 dark:text-gray-300">
                    This website uses cookies to enhance your experience and analyze site traffic.
                    {' '}
                    <Link 
                      to="/privacy" 
                      className={`underline ${themeClasses.primaryText}`}
                    >
                      Learn more
                    </Link>
                  </p>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={acceptCookies}
                  className={`py-2 px-4 rounded-md font-medium transition-colors duration-150 ${themeClasses.primaryButton} ${themeClasses.primaryButtonHover}`}
                >
                  Accept All Cookies
                </button>
                <button
                  onClick={acceptCookies}
                  className={`py-2 px-4 rounded-md font-medium border ${
                    darkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  } transition-colors duration-150`}
                >
                  Accept Necessary Only
                </button>
                <button
                  onClick={acceptCookies}
                  className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close cookie consent"
                >
                  <X size={20} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;